import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { createTaskListApi } from "../api/taskListApi";
import { createTaskApi } from "../api/taskApi";

function getRandomColor() {
    const letters = "0123456789ABCDEF";
    let color = "#";
    for (let i = 0; i < 6; i++) {
        color += letters[Math.floor(Math.random() * 16)];
    }
    return color;
}

export function useTaskLists({ role, apiUrl, getToken }) {
    const api = useMemo(
        () => createTaskListApi({ role, apiUrl, getToken }),
        [role, apiUrl, getToken]
    );

    const [lists, setLists] = useState([]);
    const [listSectionOpen, setListSectionOpen] = useState(false);
    const [popupOpen, setPopupOpen] = useState(false);
    const [availableColors, setAvailableColors] = useState([]);
    const [selectedColor, setSelectedColor] = useState(null);
    const [listName, setListName] = useState("");
    const [activeListId, setActiveListId] = useState(null);
    const [editList, setEditList] = useState(null);

    const leadOptions = useMemo(
        () =>
            lists.map((list) => ({
                label: list.list_name,
                value: list.id,
                color: list.list_color,
            })),
        [lists]
    );

    const fetchLists = useCallback(async () => {
        try {
            const data = await api.fetchLists();
            setLists(data);
        } catch (error) {
            toast.error(error.message);
        }
    }, [api]);

    useEffect(() => {
        fetchLists();
    }, [fetchLists]);

    useEffect(() => {
        if (!popupOpen) return;
        const colors = [];
        while (colors.length < 3) {
            const newColor = getRandomColor();
            if (!colors.includes(newColor) && !lists.some((l) => l.list_color === newColor)) {
                colors.push(newColor);
            }
        }
        if (editList?.list_color && !colors.includes(editList.list_color)) {
            colors[0] = editList.list_color;
        }
        setAvailableColors(colors);
    }, [popupOpen, lists, editList]);

    useEffect(() => {
        if (editList) {
            setListName(editList.list_name || "");
            setSelectedColor(editList.list_color || null);
        } else {
            setListName("");
            setSelectedColor(null);
        }
    }, [editList, popupOpen]);

    const handleCreateList = useCallback(async () => {
        if (!listName.trim() || !selectedColor) return;

        const payload = {
            list_name: listName.trim(),
            list_color: selectedColor,
        };

        try {
            if (editList) {
                await api.updateList(editList.id, payload);
                toast.success("List updated");
                setLists((prev) =>
                    prev.map((l) => (l.id === editList.id ? { ...l, ...payload } : l))
                );
                setPopupOpen(false);
                setEditList(null);
            } else {
                const newId = await api.createList(payload);
                toast.success("List created");
                setLists((prev) => [...prev, { ...payload, id: newId }]);
                setPopupOpen(false);
            }
            setListName("");
            setSelectedColor(null);
        } catch (error) {
            toast.error(error.message);
        }
    }, [api, editList, listName, selectedColor]);

    const handleDeleteList = useCallback(
        async (listId) => {
            try {
                await api.deleteList(listId);
                toast.success("List deleted");
                setLists((prev) => prev.filter((l) => l.id !== listId));
            } catch (error) {
                toast.error(error.message);
            }
        },
        [api]
    );

    return {
        lists,
        leadOptions,
        listSectionOpen,
        setListSectionOpen,
        popupOpen,
        setPopupOpen,
        availableColors,
        selectedColor,
        setSelectedColor,
        listName,
        setListName,
        activeListId,
        setActiveListId,
        editList,
        setEditList,
        handleCreateList,
        handleDeleteList,
        fetchLists,
    };
}

export function useTaskEmployees({ role, apiUrl, getToken }) {
    const api = useMemo(() => createTaskApi({ role, apiUrl, getToken }), [role, apiUrl, getToken]);
    const [assignOptions, setAssignOptions] = useState([]);

    useEffect(() => {
        if (role !== "admin") return;
        api.fetchEmployees()
            .then(setAssignOptions)
            .catch((err) => toast.error(err.message));
    }, [api, role]);

    return { assignOptions };
}
